/**
 * Calculate Pinto meals based on rules:
 * - Mon-Fri: 2 meals/day
 * - Sat: 4 meals/day
 * - Sun: 0 meals/day
 * 
 * @param {Object} pkg The pinto package object
 * @returns {Object} Calculated metrics
 */
export const calculateExactPintoMeals = (pkg) => {
  if (!pkg) return null;

  // 1. Determine total meals based on promotions
  let totalMeals = pkg.meals_total;
  let packageName = pkg.package_name || '';

  // Apply promotion quotas automatically
  const isMuscle = packageName.toLowerCase().includes('muscle') || 
                   packageName.includes('สร้างกล้าม') || 
                   packageName.includes('เพิ่มกล้าม');
  
  let expectedPromoMeals = null;
  if (packageName.includes('7 วัน')) {
    expectedPromoMeals = 15; // Promotion: 14 + 1
  } else if (packageName.includes('14 วัน') && !isMuscle) {
    expectedPromoMeals = 30; // Promotion: 28 + 2
  } else if ((packageName.includes('1 เดือน') || packageName.includes('30 วัน')) && !isMuscle) {
    expectedPromoMeals = 62; // Promotion: 60 + 2
  } else if (packageName.includes('14 วัน') && isMuscle) {
    expectedPromoMeals = 62;
  } else if ((packageName.includes('1 เดือน') || packageName.includes('30 วัน')) && isMuscle) {
    expectedPromoMeals = 124;
  }

  // Use promotion meals if DB is missing or DB value is lower than promotion
  if (expectedPromoMeals && (!totalMeals || totalMeals < expectedPromoMeals)) {
    totalMeals = expectedPromoMeals;
  }

  // Fallback if no promotion and no DB value
  if (!totalMeals) {
    const nameMatch = packageName.match(/(\d+)\s*มื้อ/);
    if (nameMatch) {
      totalMeals = parseInt(nameMatch[1], 10);
    } else {
      // If no specific name, calculate based on days
      totalMeals = (pkg.days_total || 31) * (isMuscle ? 4 : 2); 
    }
  }

  // Force correct meals for Muscle packages that might have been saved incorrectly in DB (62 instead of 124)
  if (isMuscle && (totalMeals === 60 || totalMeals === 62) && !packageName.includes('14 วัน')) {
    totalMeals = 124;
  }

  // 2. Calculate consumed meals based on start date
  let consumedMeals = 0;
  let remainingMeals = totalMeals;
  let endDate = null;
  let calendarDaysLeft = 0;

  if (pkg.start_date && pkg.status === 'active') {
    const start = new Date(pkg.start_date);
    start.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If package hasn't started yet
    if (today < start) {
      consumedMeals = 0;
      remainingMeals = totalMeals;
    } else {
      // Calculate day by day from start to today
      let currentDate = new Date(start);
      while (currentDate <= today && consumedMeals < totalMeals) {
        const dayOfWeek = currentDate.getDay(); // 0 = Sun, 6 = Sat
        
        let dailyMeals = 0;
        if (isMuscle) {
          dailyMeals = 4; // Muscle packages deliver 4 meals EVERY day
        } else {
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            dailyMeals = 2; // Mon-Fri
          } else if (dayOfWeek === 6) {
            dailyMeals = 4; // Sat
          } // Sun = 0
        }
        
        // Adjust for today if rounds haven't passed
        if (currentDate.getTime() === today.getTime() && dailyMeals > 0) {
          const currentHour = new Date().getHours();
          if (currentHour < 13) {
            dailyMeals = 0; // The single delivery round hasn't finished yet
          }
        }
        
        consumedMeals += dailyMeals;
        
        if (consumedMeals > totalMeals) {
          consumedMeals = totalMeals;
        }
        
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      remainingMeals = Math.max(0, totalMeals - consumedMeals);
    }

    // 3. Predict end date and calendar days left
    if (remainingMeals > 0) {
      let tempRemaining = remainingMeals;
      // Start counting from tomorrow if we already counted today, 
      // or from start_date if it's in the future
      let nextDate = new Date(today > start ? today : start);
      if (today >= start) {
        nextDate.setDate(nextDate.getDate() + 1);
      }

      while (tempRemaining > 0) {
        calendarDaysLeft++;
        const dayOfWeek = nextDate.getDay();
        
        let dailyMeals = 0;
        if (isMuscle) {
          dailyMeals = 4;
        } else {
          if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            dailyMeals = 2;
          } else if (dayOfWeek === 6) {
            dailyMeals = 4;
          }
        }
        
        if (dailyMeals > 0) {
          tempRemaining -= dailyMeals;
          endDate = new Date(nextDate);
        }
        
        nextDate.setDate(nextDate.getDate() + 1);
      }
    } else {
      endDate = today; // Already finished
      calendarDaysLeft = 0;
    }
  } else {
    // Inactive or no start date
    remainingMeals = pkg.meals_remaining ?? totalMeals;
    consumedMeals = totalMeals - remainingMeals;
    if (pkg.end_date) {
      endDate = new Date(pkg.end_date);
      const today = new Date();
      if (endDate > today) {
        calendarDaysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      }
    }
  }

  const progressPct = totalMeals > 0 
    ? Math.min(100, Math.max(0, (consumedMeals / totalMeals) * 100)) 
    : 0;

  return {
    totalMeals,
    consumedMeals,
    remainingMeals,
    calendarDaysLeft,
    progressPct,
    endDate: endDate ? endDate.toISOString() : null
  };
};
